import React, { useEffect, useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { IoShareSocialOutline } from "react-icons/io5";
import { BRAND_CTA_VARS } from "./brandCtaVars";

/* Brand-coloured login CTAs.
 *
 * Colours are taken STRAIGHT from the per-brand vars that App.tsx injects via
 * /theme/<FOLDER_NAME>/variables.css — nothing is invented, lightened or
 * blended here. Where a brand defines a ready-made gradient the button uses it
 * verbatim, so these CTAs match the site's other primary buttons.
 *
 * Which vars hold the brand colour differs per skin, so the names live in
 * brandCtaVars.ts and this file stays identical across skins.
 *
 * The one thing computed rather than read is the label colour, which is picked
 * black or white against the button fill. That is legibility, not styling — a
 * brand whose accent is a pale gold needs dark text on it.
 *
 * This renders on the login page of every live domain, so every path falls back
 * to the literals in brandCtaVars.ts and it must never throw.
 */

type Rgb = [number, number, number];

const channel = (v: number) => {
  const c = v / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};

const luminance = ([r, g, b]: Rgb) =>
  0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);

const contrast = (a: number, b: number) =>
  (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);

/** Pull the channels out of a computed `rgb()` / `rgba()` string. */
const parseComputedRgb = (value: string): Rgb | null => {
  const m = /rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i.exec(value);
  return m ? [+m[1], +m[2], +m[3]] : null;
};

/** Resolve ANY CSS colour token — hex, rgb(), or a bare keyword like `black` —
 *  by letting the browser do it. Custom properties are substituted verbatim, so
 *  getPropertyValue() hands back the literal token and a regex is not enough:
 *  several brands set `--primary: black`. */
const normalizeColor = (token?: string | null): Rgb | null => {
  const value = token?.trim();
  if (!value || typeof document === "undefined" || !document.body) return null;

  const probe = document.createElement("span");
  // Seed a sentinel: an invalid token leaves the assignment untouched.
  probe.style.color = "rgb(1, 2, 3)";
  probe.style.color = value;
  if (probe.style.color === "rgb(1, 2, 3)" || probe.style.color === "") {
    return null;
  }

  probe.style.display = "none";
  document.body.appendChild(probe);
  const computed = window.getComputedStyle(probe).color;
  probe.remove();
  return parseComputedRgb(computed);
};

const toHex = ([r, g, b]: Rgb) =>
  "#" +
  [r, g, b]
    .map((c) =>
      Math.round(Math.min(255, Math.max(0, c)))
        .toString(16)
        .padStart(2, "0")
    )
    .join("");

type BrandTheme = {
  accent: string;
  /** Full background-image value for the filled button. */
  gradient: string;
  ink: string;
};

const FALLBACK_THEME: BrandTheme = {
  accent: BRAND_CTA_VARS.fallbackAccent,
  gradient: `linear-gradient(135deg, ${BRAND_CTA_VARS.fallbackAccent}, ${BRAND_CTA_VARS.fallbackBase})`,
  ink: "#ffffff",
};

const resolveBrandTheme = (): BrandTheme => {
  const styles = window.getComputedStyle(document.documentElement);
  const read = (name: string) => styles.getPropertyValue(name).trim();

  // A ready-made brand gradient wins: it is the exact treatment the brand
  // already uses for its primary buttons.
  const gradientVar =
    BRAND_CTA_VARS.gradient.map(read).find((v) => v.includes("gradient(")) ?? "";

  const accentRgb =
    BRAND_CTA_VARS.accent.map((n) => normalizeColor(read(n))).find(Boolean) ??
    // No usable brand colour: take the first colour out of the brand gradient
    // so the outlined button still matches the filled one.
    (gradientVar
      ? normalizeColor((/#[0-9a-f]{3,8}|rgba?\([^)]*\)/i.exec(gradientVar) ?? [])[0])
      : null) ??
    normalizeColor(BRAND_CTA_VARS.fallbackAccent);

  if (!accentRgb) return FALLBACK_THEME;

  const baseRgb =
    BRAND_CTA_VARS.base.map((n) => normalizeColor(read(n))).find(Boolean) ??
    normalizeColor(BRAND_CTA_VARS.fallbackBase) ??
    accentRgb;

  const accent = toHex(accentRgb);
  const accentLum = luminance(accentRgb);

  return {
    accent,
    gradient:
      gradientVar || `linear-gradient(135deg, ${accent}, ${toHex(baseRgb)})`,
    // Legibility only — whichever of black/white actually reads on the fill.
    ink: contrast(accentLum, 1) >= contrast(accentLum, 0) ? "#ffffff" : "#000000",
  };
};

const SOLID_CLASSES =
  "mt-2 flex w-full cursor-pointer items-center justify-center gap-2 rounded-md " +
  "border border-[color:var(--brand-accent)] bg-[color:var(--brand-accent)] " +
  "bg-[image:var(--brand-gradient)] px-3 py-2 font-bold leading-6 " +
  "text-[color:var(--brand-ink)] animate-brand-cta-blink " +
  "transition-[filter,transform] duration-200 hover:animate-none hover:brightness-110 " +
  "hover:shadow-[0_8px_22px_-8px_var(--brand-accent)] focus-visible:animate-none " +
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 " +
  "focus-visible:outline-[color:var(--brand-accent)] active:translate-y-px " +
  "motion-reduce:animate-none";

const OUTLINE_CLASSES =
  "mt-2 flex w-full cursor-pointer items-center justify-center gap-2 rounded-md " +
  "border border-[color:var(--brand-accent)] bg-transparent px-3 py-2 font-bold leading-6 " +
  "text-[color:var(--brand-accent)] transition-[filter,transform] duration-200 " +
  "hover:brightness-110 hover:shadow-[0_8px_22px_-8px_var(--brand-accent)] " +
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 " +
  "focus-visible:outline-[color:var(--brand-accent)] active:translate-y-px";

type LoginBrandActionsProps = {
  /** REGISTRATION_WHATSAPP_LINK for the brand; button is hidden when absent. */
  whatsappLink?: string | null;
  /** Brand APK url; share button is hidden when absent. */
  apkUrl?: string | null;
};

const LoginBrandActions: React.FC<LoginBrandActionsProps> = ({
  whatsappLink,
  apkUrl,
}) => {
  const [theme, setTheme] = useState<BrandTheme>(FALLBACK_THEME);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    let frame = 0;
    const resolve = () => {
      try {
        setTheme(resolveBrandTheme());
      } catch {
        // Keep whatever we last had; the login box must still render.
      }
    };
    const schedule = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(resolve);
    };

    resolve();

    // The per-brand stylesheet is injected after mount, and some skins remove
    // and recreate that <link> rather than reusing it — so watch <head> itself
    // instead of holding a reference to any one element.
    const watched: HTMLLinkElement[] = [];
    const watch = (node: Node) => {
      if (node instanceof HTMLLinkElement && node.rel === "stylesheet") {
        node.addEventListener("load", schedule);
        watched.push(node);
      }
    };
    document.head.querySelectorAll("link[rel='stylesheet']").forEach(watch);

    const observer = new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach(watch));
      schedule();
    });
    observer.observe(document.head, { childList: true });

    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(frame);
      watched.forEach((node) => node.removeEventListener("load", schedule));
    };
  }, []);

  if (!whatsappLink && !apkUrl) return null;

  // Rendered as a real <a href> so a mobile long-press offers the browser's
  // link menu (copy / share / open in new tab); taps still go via window.open
  // so behaviour inside the APK WebView is unchanged.
  const contactOnWhatsApp = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (!whatsappLink) return;
    window.open(whatsappLink, "_blank");
  };

  // Hands the whole invite to the OS share sheet rather than building a wa.me
  // URL. Nothing is percent-encoded and no URL crosses an app boundary, so the
  // emoji travel as a plain JS string.
  const shareApkLink = async () => {
    if (!apkUrl) return;
    const url = apkUrl.startsWith("http") ? apkUrl : "https://" + apkUrl;
    const shareText =
      "Experience the best in one app! ✨\n" +
      "Install the Android app today 👇\n" +
      url;

    if (navigator.share) {
      try {
        await navigator.share({ text: shareText });
      } catch (error) {
        console.log("Share cancelled", error);
      }
      return;
    }

    try {
      await navigator.clipboard.writeText(shareText);
      alert("Share text copied");
    } catch {
      // Clipboard is permission-gated and throws when denied; without this the
      // await rejects unhandled and the button looks dead.
      window.prompt("Copy this invite:", shareText);
    }
  };

  return (
    <div
      className="w-full"
      style={
        {
          "--brand-accent": theme.accent,
          "--brand-gradient": theme.gradient,
          "--brand-ink": theme.ink,
        } as React.CSSProperties
      }
    >
      {whatsappLink && (
        <a
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          className={`${SOLID_CLASSES} no-underline`}
          onClick={contactOnWhatsApp}
        >
          <FaWhatsapp className="w-5 h-5" />
          <span>Contact On WhatsApp</span>
        </a>
      )}

      {apkUrl && (
        <button type="button" className={OUTLINE_CLASSES} onClick={shareApkLink}>
          <IoShareSocialOutline className="w-5 h-5" />
          <span>Share This APK With Friends</span>
        </button>
      )}
    </div>
  );
};

export default LoginBrandActions;
